import { SPACING } from '@alto/design-library-tokens';
import { Button, MdPadding } from '@alto/design-system';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { useNextAvailableDateAddressId } from '~shared/features/next-available-date/hooks/useNextAvailableDateAddressId';
import { getNextAvailableDatesByPrescriptionID } from '~shared/features/next-available-date/selectors/getNextAvailableDatesByPrescriptionID';
import { queries } from '~shared/queries/query-keys';
import { useSelectorShared } from '~shared/store';
import { BackorderedWarning, useBackorderedMedications } from '../../backordered-medications';
import { useEssentialsCartThreshold } from '../../essentials';
import { CartFooterDescriptionText } from './CartFooterDescriptionText';
import { CartMixedAvailabilityAlert } from './CartMixedAvailabilityAlert';

type Props = {
  readonly handleCheckout: () => void;
  readonly visible: boolean;
};

export const CartFixedFooter = ({ handleCheckout, visible }: Props) => {
  const { showEssentialsThreshold } = useEssentialsCartThreshold();
  const { prescriptionIDs } = usePrescriptionsInCart();
  const { deliveryMethods } = useCartDeliveryMethods();
  const isSplitShipments = deliveryMethods.length > 1;
  const { isFetching } = useQuery({
    ...queries.enterCheckout.enterCheckout({ cart_prescription_ids: prescriptionIDs }),
    enabled: false,
  });
  const backorderedMedicationIds = useBackorderedMedications(prescriptionIDs);
  const hasBackorderedMedications = backorderedMedicationIds.length > 0;

  // Determine if we need to show the mixed next available date alert.
  const nextAvailableDateAddressID = useNextAvailableDateAddressId();
  const nextAvailableDatesByPrescriptionID = useSelectorShared((state) =>
    getNextAvailableDatesByPrescriptionID(state, { nextAvailableDateAddressID }),
  );
  const earliestNextAvailableDates = prescriptionIDs.map((prescriptionId) => {
    return nextAvailableDatesByPrescriptionID[prescriptionId]?.earliest.date;
  }, []);
  const hasMixedNextAvailableDate = earliestNextAvailableDates.some((date) => date !== earliestNextAvailableDates[0]);
  const maxDate = earliestNextAvailableDates.reduce((a, b) => (a > b ? a : b), '');

  return visible ? (
    <>
      {hasMixedNextAvailableDate && maxDate ? <CartMixedAvailabilityAlert date={maxDate} /> : null}
      {hasBackorderedMedications ? (
        <MdPadding topPadding={SPACING.STATIC.NONE}>
          <BackorderedWarning prescriptionIds={backorderedMedicationIds} />
        </MdPadding>
      ) : null}
      {!hasMixedNextAvailableDate ? <CartFooterDescriptionText hideNextAvailableDate={isSplitShipments} /> : null}
      <Button
        disabled={showEssentialsThreshold || hasBackorderedMedications}
        onPress={handleCheckout}
        label="Checkout"
        loading={isFetching}
      />
    </>
  ) : null;
};
